'use client';

import React, { useState } from 'react';
import { Clock, RefreshCw, DollarSign, Percent, TrendingUp, Play } from 'lucide-react';
import { colors, baseStyles } from './SharedUI'; 

// Hàm phụ trợ format thời gian
const formatDateTime = (timestamp) => {
  const d = new Date(timestamp);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
};

// Hàm format giá
const formatPrice = (price) => {
  const p = parseFloat(price);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(p);
};

export default function FundingRateHistory({ defaultSymbol = 'BTCUSDT' }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [timeRange, setTimeRange] = useState(12); // Default là 12h
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false); // Thêm state để nhận biết đã bấm nút hay chưa

  const fetchFundingData = async () => {
    if (!symbol.trim()) return;
    setIsLoading(true);
    setHasFetched(true);

    try {
      const cleanSymbol = symbol.trim().toUpperCase();
      const endTime = Date.now();
      const startTime = endTime - (timeRange * 60 * 60 * 1000); // Lùi về quá khứ theo số giờ

      // Gọi API Binance Futures Funding Rate
      // const endpoint = `https://fapi.binance.com/fapi/v1/fundingRate?symbol=${cleanSymbol}&startTime=${startTime}&endTime=${endTime}&limit=100`;
      const endpoint = `https://fapi.binance.com/fapi/v1/fundingRate?symbol=${cleanSymbol}&limit=100`;
      
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Không thể tải dữ liệu Funding Rate');
      
      const result = await response.json();
      
      // Đảo ngược mảng để giờ mới nhất hiện lên trên cùng
      setData(result.reverse());
    } catch (error) {
      alert('Có lỗi xảy ra: ' + error.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: colors.bg || '#0b0e14', color: '#ffffff', padding: '20px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
      <div style={{ margin: '0 auto', ...baseStyles.card, padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* Header & Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Percent size={24} color={colors.warn || '#fbbf24'} />
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>LỊCH SỬ FUNDING RATE</h2>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Theo dõi tỷ lệ phí và giá thanh toán</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input 
              type="text" 
              value={symbol} 
              onChange={(e) => setSymbol(e.target.value)} 
              placeholder="VD: BTCUSDT"
              style={{ ...baseStyles.input, width: '120px', textTransform: 'uppercase', textAlign: 'center', fontWeight: 'bold' }} 
              disabled={isLoading}
            />

            {/* Nút Toggle 12H / 24H (Giờ chỉ thay đổi state, KHÔNG tự gọi API) */}
            <div style={{ display: 'flex', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '6px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
              <button
                onClick={() => setTimeRange(12)}
                disabled={isLoading}
                style={{
                  padding: '8px 16px', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px',
                  backgroundColor: timeRange === 12 ? (colors.blue || '#3b82f6') : 'transparent',
                  color: timeRange === 12 ? '#fff' : colors.textSub,
                  transition: 'all 0.2s'
                }}
              >
                12 Giờ
              </button>
              <button
                onClick={() => setTimeRange(24)}
                disabled={isLoading}
                style={{
                  padding: '8px 16px', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px',
                  backgroundColor: timeRange === 24 ? (colors.blue || '#3b82f6') : 'transparent',
                  color: timeRange === 24 ? '#fff' : colors.textSub,
                  transition: 'all 0.2s'
                }}
              >
                24 Giờ
              </button>
            </div>

            {/* NÚT THỰC THI (Gọi API) */}
            <button 
              onClick={fetchFundingData}
              disabled={isLoading}
              style={{ 
                ...baseStyles.select, 
                backgroundColor: isLoading ? colors.border : '#65a30d', 
                color: '#fff', border: 'none', padding: '8px 16px', 
                cursor: isLoading ? 'not-allowed' : 'pointer', 
                display: 'flex', alignItems: 'center', gap: '8px', 
                fontWeight: 'bold', borderRadius: '6px', transition: 'all 0.2s'
              }}
            >
              {isLoading ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
              {isLoading ? 'ĐANG KÉO...' : 'LẤY DỮ LIỆU'}
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textSub, fontWeight: 'bold' }}>
              Đang tải dữ liệu...
            </div>
          ) : !hasFetched ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textSub, fontWeight: 'bold' }}>
              Vui lòng chọn khung thời gian và bấm "Lấy Dữ Liệu" để xem lịch sử.
            </div>
          ) : data.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textSub, fontWeight: 'bold' }}>
              Không có dữ liệu Funding Rate trong khoảng thời gian này.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(0,0,0,0.4)', borderBottom: `2px solid ${colors.border}` }}>
                  <th style={{ padding: '12px 16px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Clock size={14}/> Thời Gian Thanh Toán</div>
                  </th>
                  <th style={{ padding: '12px 16px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><TrendingUp size={14}/> Funding Rate</div>
                  </th>
                  <th style={{ padding: '12px 16px', fontSize: '13px', color: colors.textSub, fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><DollarSign size={14}/> Mark Price</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.slice(0, timeRange).map((item, index) => {
                  const rate = parseFloat(item.fundingRate);
                  const isPositive = rate > 0;
                  const isNegative = rate < 0;
                  
                  return (
                    <tr key={index} style={{ borderBottom: `1px solid rgba(255,255,255,0.05)`, transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontFamily: 'monospace', color: '#e2e8f0' }}>
                        {formatDateTime(item.fundingTime)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                        <span style={{ 
                          color: isPositive ? (colors.green || '#10b981') : (isNegative ? (colors.red || '#ef4444') : '#94a3b8'),
                          backgroundColor: isPositive ? 'rgba(16, 185, 129, 0.1)' : (isNegative ? 'rgba(239, 68, 68, 0.1)' : 'transparent'),
                          padding: '4px 8px', borderRadius: '4px'
                        }}>
                          {(rate * 100).toFixed(4)}%
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace', color: '#f8fafc' }}>
                        {formatPrice(item.markPrice)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ fontSize: '11px', color: colors.textSub, fontStyle: 'italic', lineHeight: '1.5' }}>
          * Funding Rate dương: Phe Long trả phí cho phe Short.<br/>
          * Funding Rate âm: Phe Short trả phí cho phe Long.<br/>
          * Khoảng thời gian trả funding phụ thuộc vào từng đồng coin (thường là mỗi 8h hoặc 4h).
        </div>
      </div>
    </div>
  );
}
